import { initializeApp, getApp, deleteApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../config/firebase';
import { ref, get, update, set, push, remove } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import {
  buildAchievementsPayload,
  buildStudentJourneyState,
} from './lmsSelectors';
import {
  buildAcademyStudentId,
  buildStudentQrResolverUrl,
} from './studentIdentity';
import { invalidateAdminCache, invalidateStudentCache } from '../services/lmsDataCache';

const touchLmsCache = ({ studentId, admin = true } = {}) => {
  if (admin) invalidateAdminCache();
  if (studentId) invalidateStudentCache(studentId);
};

const appendJoinedCourse = async ({
  studentId,
  courseId,
  batchId,
  batchIndex,
  branchId,
  branchName,
  enrollmentId,
  enrollmentDate,
}) => {
  const studentCoursesRef = ref(db, `students/${studentId}/joinedCourses`);
  const snapshot = await get(studentCoursesRef);
  const joinedCourses = snapshot.val() || [];

  const alreadyExists = joinedCourses.some(
    (course) =>
      course.courseId === courseId &&
      String(course.batchId) === String(batchId) &&
      (batchIndex == null || String(course.batchIndex ?? '') === String(batchIndex)),
  );

  if (alreadyExists) return;

  await set(studentCoursesRef, [
    ...joinedCourses,
    {
      courseId,
      batchId,
      batchIndex: batchIndex ?? null,
      branchId: branchId || '',
      branchName: branchName || '',
      enrollmentId,
      enrollmentDate,
      status: 'active',
    },
  ]);
};

const removeJoinedCourse = async ({ studentId, courseId, batchId, batchIndex }) => {
  const studentCoursesRef = ref(db, `students/${studentId}/joinedCourses`);
  const snapshot = await get(studentCoursesRef);
  const joinedCourses = snapshot.val() || [];

  await set(
    studentCoursesRef,
    joinedCourses.filter(
      (course) =>
        course.courseId !== courseId ||
        String(course.batchId) !== String(batchId) ||
        (batchIndex != null && String(course.batchIndex ?? '') !== String(batchIndex)),
    ),
  );
};

const updateJoinedCourseBatch = async ({
  studentId,
  courseId,
  oldBatchId,
  newBatchId,
  batchIndex,
  branchId,
  branchName,
}) => {
  const studentCoursesRef = ref(db, `students/${studentId}/joinedCourses`);
  const snapshot = await get(studentCoursesRef);
  const joinedCourses = snapshot.val() || [];
  const nextCourses = joinedCourses.map((course) => {
    const matches =
      course.courseId === courseId &&
      String(course.batchId) === String(oldBatchId) &&
      (batchIndex == null || String(course.batchIndex ?? '') === String(batchIndex));
    return matches
      ? {
          ...course,
          batchId: newBatchId,
          batchIndex: batchIndex ?? course.batchIndex ?? null,
          branchId: branchId || course.branchId || '',
          branchName: branchName || course.branchName || '',
        }
      : course;
  });

  await set(studentCoursesRef, nextCourses);
};

const syncBatchEnrollmentList = async ({
  courseId,
  batchId,
  batchIndex,
  branchId,
  studentId,
  action,
}) => {
  const courseRef = ref(db, `courses/${courseId}`);
  const courseSnapshot = await get(courseRef);
  const course = courseSnapshot.val();

  if (!course?.batches) return;

  const targetBatchIndex = course.batches.findIndex(
    (batch, index) => {
      if (batchIndex != null && Number(batchIndex) === index) return true;
      const batchMatches =
        String(batch.batchId) === String(batchId) || String(index) === String(batchId);
      const branchMatches =
        !branchId || !batch.branchId || String(batch.branchId) === String(branchId);
      return batchMatches && branchMatches;
    },
  );

  if (targetBatchIndex === -1) return;

  const batch = course.batches[targetBatchIndex];
  const enrolledStudents = Array.isArray(batch.enrolledStudents)
    ? batch.enrolledStudents
    : [];

  const nextStudents =
    action === 'add'
      ? enrolledStudents.includes(studentId)
        ? enrolledStudents
        : [...enrolledStudents, studentId]
      : enrolledStudents.filter((id) => id !== studentId);

  course.batches[targetBatchIndex] = {
    ...batch,
    enrolledStudents: nextStudents,
  };

  await update(courseRef, {
    batches: course.batches,
  });
};

export const submitEnrollmentRequest = async ({
  courseId,
  batchId,
  currentUser,
  courseName,
  additionalInfo = {},
}) => {
  const requestRef = ref(db, 'enrollmentRequests');
  const newRequestRef = push(requestRef);
  const requestId = newRequestRef.key;
  const courseSnapshot = await get(ref(db, `courses/${courseId}`));
  const course = courseSnapshot.val() || {};
  const batch = (course.batches || []).find(
    (item, index) =>
      String(item.batchId) === String(batchId) || String(index) === String(batchId),
  );

  let attachmentUrl = '';
  if (additionalInfo.file) {
    const fileRef = storageRef(storage, `paymentSlips/${requestId}`);
    await uploadBytes(fileRef, additionalInfo.file);
    attachmentUrl = await getDownloadURL(fileRef);
  }

  await set(newRequestRef, {
    id: requestId,
    courseId,
    batchId,
    studentId: currentUser.uid,
    studentName:
      currentUser.displayName ||
      `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
    courseName,
    status: 'pending',
    requestDate: new Date().toISOString(),
    receiptNumber: additionalInfo.receiptNumber || '',
    note: additionalInfo.note || '',
    attachmentUrl,
    paymentPlan: additionalInfo.paymentPlan || 'pay_later',
  });

  const paymentRequestRef = push(ref(db, 'paymentRequests'));
  const paymentRequestId = paymentRequestRef.key;
  const dueDate =
    additionalInfo.dueDate ||
    buildRegistrationDueDate(batch?.startDate, additionalInfo.paymentPlan);

  await set(paymentRequestRef, {
    id: paymentRequestId,
    courseId,
    courseName,
    batchId,
    batchName: batch?.batchId || batchId,
    studentId: currentUser.uid,
    studentName:
      currentUser.displayName ||
      `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
    amount: Number(course.fees?.registrationFee || 0),
    paymentType: 'registration',
    dueDate,
    notes: additionalInfo.note || '',
    createdAt: new Date().toISOString(),
    status: 'pending',
    requestId,
  });

  if (additionalInfo.paymentPlan === 'pay_now') {
    const paymentRef = ref(db, 'payments');
    await push(paymentRef, {
      studentId: currentUser.uid,
      studentName:
        currentUser.displayName ||
        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
      courseId,
      courseName,
      batchId,
      batchName: batch?.batchId || batchId,
      amount: Number(course.fees?.registrationFee || 0),
      paymentType: 'registration',
      paymentDate: new Date().toISOString(),
      paymentMethod: additionalInfo.paymentMethod || 'bank_transfer',
      receiptNumber: additionalInfo.receiptNumber || '',
      receiptUrl: attachmentUrl,
      notes: additionalInfo.note || '',
      status: 'pending',
      createdAt: new Date().toISOString(),
      requestId: paymentRequestId,
    });

    await update(ref(db, `paymentRequests/${paymentRequestId}`), {
      status: 'processing',
      paymentSubmitted: true,
      paymentDate: new Date().toISOString(),
    });
  }

  touchLmsCache({ studentId: currentUser.uid, admin: true });
  return requestId;
};

const buildRegistrationDueDate = (startDate, paymentPlan) => {
  if (paymentPlan === 'pay_now') {
    return new Date().toISOString().split('T')[0];
  }

  if (startDate?.year && startDate?.month && startDate?.date) {
    return new Date(startDate.year, startDate.month - 1, startDate.date)
      .toISOString()
      .split('T')[0];
  }

  const due = new Date();
  due.setDate(due.getDate() + 7);
  return due.toISOString().split('T')[0];
};

export const approveEnrollmentRequest = async (request, adminUser) => {
  const enrollmentDate = new Date().toISOString();
  const newEnrollmentRef = push(ref(db, 'enrollments'));

  await update(ref(db, `enrollmentRequests/${request.id}`), {
    status: 'approved',
    updatedAt: enrollmentDate,
    reviewedBy: adminUser?.uid || null,
  });

  await set(newEnrollmentRef, {
    courseId: request.courseId,
    batchId: request.batchId,
    studentId: request.studentId,
    enrollmentDate,
    status: 'active',
    progress: 0,
  });

  await appendJoinedCourse({
    studentId: request.studentId,
    courseId: request.courseId,
    batchId: request.batchId,
    enrollmentId: newEnrollmentRef.key,
    enrollmentDate,
  });

  await syncBatchEnrollmentList({
    courseId: request.courseId,
    batchId: request.batchId,
    studentId: request.studentId,
    action: 'add',
  });

  touchLmsCache({ studentId: request.studentId });
  return newEnrollmentRef.key;
};

export const declineEnrollmentRequest = async (requestId, adminUser) => {
  await update(ref(db, `enrollmentRequests/${requestId}`), {
    status: 'declined',
    updatedAt: new Date().toISOString(),
    reviewedBy: adminUser?.uid || null,
  });
  touchLmsCache();
};

const resolveBatchForAssignment = async ({ courseId, batchId, batchIndex, branchId }) => {
  const courseSnapshot = await get(ref(db, `courses/${courseId}`));
  const course = courseSnapshot.val() || {};
  const batches = Array.isArray(course.batches) ? course.batches : [];
  const index = batches.findIndex((batch, currentIndex) => {
    if (batchIndex != null && Number(batchIndex) === currentIndex) return true;
    const batchMatches =
      String(batch.batchId) === String(batchId) || String(currentIndex) === String(batchId);
    const branchMatches =
      !branchId || !batch.branchId || String(batch.branchId) === String(branchId);
    return batchMatches && branchMatches;
  });
  const batch = index >= 0 ? batches[index] : {};

  return {
    batch,
    batchIndex: index >= 0 ? index : batchIndex ?? null,
    batchId: batch?.batchId || batchId,
    branchId: batch?.branchId || branchId || '',
    branchName: batch?.branchName || '',
    branch: batch?.branch || '',
  };
};

export const assignStudentToBatch = async ({
  studentId,
  courseId,
  batchId,
  batchIndex,
  branchId,
}) => {
  const enrollmentDate = new Date().toISOString();
  const resolvedBatch = await resolveBatchForAssignment({
    courseId,
    batchId,
    batchIndex,
    branchId,
  });
  const enrollmentsSnapshot = await get(ref(db, 'enrollments'));
  const existingEnrollmentEntry = Object.entries(enrollmentsSnapshot.val() || {}).find(
    ([, enrollment]) =>
      enrollment?.studentId === studentId &&
      enrollment?.courseId === courseId &&
      String(enrollment?.batchId) === String(resolvedBatch.batchId) &&
      (resolvedBatch.batchIndex == null ||
        enrollment?.batchIndex == null ||
        String(enrollment.batchIndex) === String(resolvedBatch.batchIndex)) &&
      (!resolvedBatch.branchId ||
        !enrollment?.branchId ||
        String(enrollment.branchId) === String(resolvedBatch.branchId)) &&
      enrollment?.status === 'active',
  );

  if (existingEnrollmentEntry) {
    return existingEnrollmentEntry[0];
  }

  const newEnrollmentRef = push(ref(db, 'enrollments'));

  await set(newEnrollmentRef, {
    courseId,
    batchId: resolvedBatch.batchId,
    batchIndex: resolvedBatch.batchIndex,
    branchId: resolvedBatch.branchId,
    branchName: resolvedBatch.branchName,
    branch: resolvedBatch.branch,
    studentId,
    enrollmentDate,
    status: 'active',
    progress: 0,
  });

  await appendJoinedCourse({
    studentId,
    courseId,
    batchId: resolvedBatch.batchId,
    batchIndex: resolvedBatch.batchIndex,
    branchId: resolvedBatch.branchId,
    branchName: resolvedBatch.branchName,
    enrollmentId: newEnrollmentRef.key,
    enrollmentDate,
  });

  await syncBatchEnrollmentList({
    courseId,
    batchId: resolvedBatch.batchId,
    batchIndex: resolvedBatch.batchIndex,
    branchId: resolvedBatch.branchId,
    studentId,
    action: 'add',
  });

  touchLmsCache({ studentId });
  return newEnrollmentRef.key;
};

export const removeEnrollmentRecord = async ({
  enrollmentId,
  studentId,
  courseId,
  batchId,
  batchIndex,
  branchId,
}) => {
  await remove(ref(db, `enrollments/${enrollmentId}`));
  await removeJoinedCourse({ studentId, courseId, batchId, batchIndex });
  await syncBatchEnrollmentList({
    courseId,
    batchId,
    batchIndex,
    branchId,
    studentId,
    action: 'remove',
  });
  touchLmsCache({ studentId });
};

export const renameCourseBatchAssignments = async ({
  courseId,
  batchIndex,
  oldBatchId,
  newBatchId,
  branchId = '',
  branchName = '',
}) => {
  if (!courseId || oldBatchId === newBatchId) return { updatedEnrollments: 0 };

  const enrollmentsSnapshot = await get(ref(db, 'enrollments'));
  const enrollmentsData = enrollmentsSnapshot.val() || {};
  const updates = {};
  const joinedCourseUpdates = [];
  let updatedEnrollments = 0;

  Object.entries(enrollmentsData).forEach(([enrollmentId, enrollment]) => {
    const matches =
      enrollment?.courseId === courseId &&
      String(enrollment?.batchId) === String(oldBatchId) &&
      (batchIndex == null ||
        enrollment?.batchIndex == null ||
        String(enrollment.batchIndex) === String(batchIndex));

    if (!matches) return;

    updates[`enrollments/${enrollmentId}/batchId`] = newBatchId;
    updates[`enrollments/${enrollmentId}/batchIndex`] = batchIndex ?? null;
    updates[`enrollments/${enrollmentId}/branchId`] = branchId || enrollment.branchId || '';
    updates[`enrollments/${enrollmentId}/branchName`] =
      branchName || enrollment.branchName || '';
    updatedEnrollments += 1;
    joinedCourseUpdates.push(
      updateJoinedCourseBatch({
        studentId: enrollment.studentId,
        courseId,
        oldBatchId,
        newBatchId,
        batchIndex,
        branchId,
        branchName,
      }),
    );
  });

  if (Object.keys(updates).length) {
    await update(ref(db), updates);
  }
  await Promise.all(joinedCourseUpdates);

  touchLmsCache();
  return { updatedEnrollments };
};

export const submitPaymentRecord = async ({
  paymentRequest,
  formData,
  receiptFile,
}) => {
  let receiptUrl = '';
  if (receiptFile) {
    const fileRef = storageRef(
      storage,
      `receipts/${paymentRequest.id}_${receiptFile.name}`,
    );
    await uploadBytes(fileRef, receiptFile);
    receiptUrl = await getDownloadURL(fileRef);
  }

  const paymentRef = ref(db, 'payments');
  await push(paymentRef, {
    studentId: paymentRequest.studentId || '',
    studentName: paymentRequest.studentName || '',
    courseId: paymentRequest.courseId || '',
    courseName: paymentRequest.courseName || '',
    batchId: paymentRequest.batchId || '',
    batchName: paymentRequest.batchName || '',
    amount: paymentRequest.amount || 0,
    paymentType: paymentRequest.paymentType || '',
    installmentNumber: paymentRequest.installmentNumber || null,
    totalInstallments: paymentRequest.totalInstallments || null,
    paymentDate: new Date().toISOString(),
    paymentMethod: formData.paymentMethod,
    receiptNumber: formData.receiptNumber,
    receiptUrl,
    notes: formData.notes || '',
    status: 'pending',
    createdAt: new Date().toISOString(),
    requestId: paymentRequest.id,
  });

  await update(ref(db, `paymentRequests/${paymentRequest.id}`), {
    status: 'processing',
    paymentSubmitted: true,
    paymentDate: new Date().toISOString(),
  });
  touchLmsCache({ studentId: paymentRequest.studentId, admin: true });
};

export const verifyPaymentRecord = async ({ payment, adminUser, isApproved }) => {
  const paymentRef = ref(db, `payments/${payment.id}`);

  if (isApproved) {
    const paymentRequestRef = ref(db, `paymentRequests/${payment.requestId}`);
    const paymentRequestSnapshot = await get(paymentRequestRef);
    const paymentRequest = paymentRequestSnapshot.val();
    const timestamp = new Date().toISOString();
    const verifiedPaymentData = {
      ...payment,
      status: 'completed',
      verifiedAt: timestamp,
      verifiedBy: adminUser.uid,
      requestDetails: {
        createdAt: paymentRequest?.createdAt,
        dueDate: paymentRequest?.dueDate,
        requestId: payment.requestId,
      },
      verificationDetails: {
        adminId: adminUser.uid,
        adminName: adminUser.displayName || adminUser.email,
        verifiedAt: timestamp,
      },
    };

    await update(paymentRef, verifiedPaymentData);
    await update(ref(db, `verifiedPayments/${payment.id}`), verifiedPaymentData);

    if (payment.requestId) {
      await remove(paymentRequestRef);
    }
  } else {
    await update(paymentRef, {
      status: 'failed',
      verifiedAt: new Date().toISOString(),
      verifiedBy: adminUser.uid,
      failureReason: 'Declined by admin',
    });

    if (payment.requestId) {
      await update(ref(db, `paymentRequests/${payment.requestId}`), {
        status: 'pending',
        lastFailedAt: new Date().toISOString(),
        failureReason: 'Payment verification failed',
      });
    }
  }
  touchLmsCache({ studentId: payment.studentId, admin: true });
};

const ACHIEVEMENTS_SYNC_COOLDOWN_MS = 5 * 60 * 1000;

export const syncStudentAchievements = async ({
  user,
  student,
  courseData,
  force = false,
}) => {
  if (!user?.uid) return;

  const cooldownKey = `lms-achievements-sync:${user.uid}`;
  if (!force && typeof window !== 'undefined') {
    const lastSync = Number(window.sessionStorage.getItem(cooldownKey) || 0);
    if (lastSync && Date.now() - lastSync < ACHIEVEMENTS_SYNC_COOLDOWN_MS) {
      return;
    }
  }

  let enrollments = courseData?.enrollments || [];
  let enrollmentRequests = courseData?.enrollmentRequests || [];
  let paymentRequests = courseData?.paymentRequests || [];
  let payments = courseData?.payments || [];
  let courses = courseData?.courses || {};

  if (!courseData) {
    const { loadStudentScopedRecords, loadStudentWorkspaceRaw } = await import(
      '../services/lmsDataLoaders'
    );
    const [scoped, workspace] = await Promise.all([
      loadStudentScopedRecords(user.uid),
      loadStudentWorkspaceRaw(user.uid),
    ]);
    enrollments = scoped.enrollments;
    enrollmentRequests = scoped.enrollmentRequests;
    paymentRequests = scoped.paymentRequests;
    payments = scoped.payments;
    courses = workspace.courses;
  }

  const journeyState = buildStudentJourneyState({
    user,
    student,
    enrollments,
    enrollmentRequests,
    paymentRequests,
    payments,
    courses,
  });

  const nextAchievements = buildAchievementsPayload(journeyState);
  const currentAchievements = user.achievements || {};
  const unchanged = Object.keys(nextAchievements).every((key) => {
    const next = nextAchievements[key];
    const current = currentAchievements[key];
    return Boolean(current?.unlocked) === Boolean(next?.unlocked);
  });

  if (unchanged && Object.keys(currentAchievements).length > 0 && !force) {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(cooldownKey, String(Date.now()));
    }
    return;
  }

  await update(ref(db, `users/${user.uid}`), {
    achievements: nextAchievements,
  });

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(cooldownKey, String(Date.now()));
  }
};

export const ensureStudentAcademyId = async ({ userId }) => {
  if (!userId) return '';

  const [userSnapshot, studentSnapshot] = await Promise.all([
    get(ref(db, `users/${userId}`)),
    get(ref(db, `students/${userId}`)),
  ]);

  const user = userSnapshot.val() || {};
  const student = studentSnapshot.val() || {};
  const existingId = student.academyStudentId || user.academyStudentId;
  if (existingId) return existingId;

  const academyStudentId = buildAcademyStudentId(userId, user.createdAt || student.createdAt);
  const registrationDate = student.registrationDate || user.createdAt || new Date().toISOString();

  await Promise.all([
    update(ref(db, `users/${userId}`), {
      academyStudentId,
      updatedAt: new Date().toISOString(),
    }),
    update(ref(db, `students/${userId}`), {
      academyStudentId,
      registrationDate,
      updatedAt: new Date().toISOString(),
      userId,
      status: student.status || 'active',
    }),
  ]);

  return academyStudentId;
};

export const markAttendanceFromReception = async ({
  studentId,
  academyStudentId,
  studentName,
  courseId,
  courseName,
  batchId,
  batchName,
  branch,
  operator,
  notes = '',
}) => {
  const attendanceRef = push(ref(db, `attendance/${studentId}`));
  const timestamp = new Date().toISOString();
  await set(attendanceRef, {
    id: attendanceRef.key,
    studentId,
    academyStudentId,
    studentName,
    courseId,
    courseName,
    batchId: batchId || '',
    batchName: batchName || '',
    branch: branch || '',
    date: timestamp,
    markedAt: timestamp,
    markedById: operator?.uid || '',
    markedByName:
      operator?.displayName ||
      `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
      operator?.email ||
      'Reception',
    status: 'present',
    notes,
    source: 'reception_kiosk',
  });
};

export const markBatchAttendance = async ({
  studentId,
  academyStudentId = '',
  studentName = '',
  courseId,
  courseName = '',
  batchId = '',
  batchName = '',
  branch = '',
  periodStart,
  periodEnd = '',
  status = 'present',
  notes = '',
  operator,
}) => {
  if (!studentId || !courseId || !periodStart) {
    throw new Error('Student, course, and period are required for attendance.');
  }

  const attendanceRootRef = ref(db, `attendance/${studentId}`);
  const snapshot = await get(attendanceRootRef);
  const existingEntry = Object.entries(snapshot.val() || {}).find(([, record]) => {
    const sameCourse = String(record?.courseId || '') === String(courseId);
    const sameBatch =
      !batchId ||
      !record?.batchId ||
      String(record.batchId) === String(batchId);
    return sameCourse && sameBatch && record?.periodStart === periodStart;
  });

  if (status === 'clear') {
    if (existingEntry) {
      await remove(ref(db, `attendance/${studentId}/${existingEntry[0]}`));
    }
    return null;
  }

  const timestamp = new Date().toISOString();
  const operatorName =
    operator?.displayName ||
    `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
    operator?.email ||
    'Admin';
  const recordRef = existingEntry
    ? ref(db, `attendance/${studentId}/${existingEntry[0]}`)
    : push(attendanceRootRef);
  const recordId = existingEntry?.[0] || recordRef.key;

  await set(recordRef, {
    id: recordId,
    studentId,
    academyStudentId,
    studentName,
    courseId,
    courseName,
    batchId,
    batchName,
    branch,
    date: periodStart,
    periodStart,
    periodEnd,
    markedAt: timestamp,
    markedById: operator?.uid || '',
    markedByName: operatorName,
    status,
    notes,
    source: 'admin_batch_ledger',
  });

  touchLmsCache({ studentId });
  return recordId;
};

export const recordBatchPayment = async ({
  studentId,
  studentName = '',
  courseId,
  courseName = '',
  batchId = '',
  batchName = '',
  amount,
  paymentMethod = 'cash',
  receiptNumber = '',
  receiptFile,
  notes = '',
  paymentCycle = '',
  paymentCycleLabel = '',
  operator,
}) => {
  if (!studentId || !courseId) {
    throw new Error('Student and course are required for payment.');
  }
  const numericAmount = Number(amount || 0);
  if (!numericAmount) {
    throw new Error('Payment amount must be greater than zero.');
  }

  const timestamp = new Date().toISOString();
  const operatorName =
    operator?.displayName ||
    `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
    operator?.email ||
    'Admin';

  const paymentRef = push(ref(db, 'payments'));
  const paymentId = paymentRef.key;
  let receiptUrl = '';
  if (receiptFile) {
    const fileRef = storageRef(
      storage,
      `batchLedgerReceipts/${paymentId}_${receiptFile.name}`,
    );
    await uploadBytes(fileRef, receiptFile);
    receiptUrl = await getDownloadURL(fileRef);
  }
  const paymentData = {
    id: paymentId,
    studentId,
    studentName,
    courseId,
    courseName,
    batchId,
    batchName,
    amount: numericAmount,
    paymentType: 'batch_payment',
    paymentCycle,
    paymentCycleLabel,
    paymentDate: timestamp,
    paymentMethod,
    receiptNumber,
    receiptUrl,
    notes,
    createdAt: timestamp,
    recordedFromBatchLedger: true,
    recordedById: operator?.uid || '',
    recordedByName: operatorName,
    status: 'completed',
    verifiedAt: timestamp,
    verifiedBy: operator?.uid || '',
    verificationDetails: {
      adminId: operator?.uid || '',
      adminName: operatorName,
      verifiedAt: timestamp,
    },
  };

  await set(paymentRef, paymentData);
  await set(ref(db, `verifiedPayments/${paymentId}`), paymentData);

  touchLmsCache({ studentId });
  return paymentId;
};

export const recordReceptionPayment = async ({
  paymentRequest,
  paymentMethod,
  receiptNumber = '',
  notes = '',
  receiptFile,
  paymentType = '',
  paymentCycle = '',
  paymentCycleLabel = '',
  operator,
}) => {
  let receiptUrl = '';
  if (receiptFile) {
    const fileRef = storageRef(
      storage,
      `receptionReceipts/${paymentRequest.id}_${receiptFile.name}`,
    );
    await uploadBytes(fileRef, receiptFile);
    receiptUrl = await getDownloadURL(fileRef);
  }

  const timestamp = new Date().toISOString();
  const operatorName =
    operator?.displayName ||
    `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
    operator?.email ||
    'Reception';

  const paymentPayload = {
    studentId: paymentRequest.studentId || '',
    studentName: paymentRequest.studentName || '',
    courseId: paymentRequest.courseId || '',
    courseName: paymentRequest.courseName || '',
    batchId: paymentRequest.batchId || '',
    batchName: paymentRequest.batchName || '',
    amount: Number(paymentRequest.amount || 0),
    paymentType: paymentType || paymentRequest.paymentType || 'payment',
    paymentCycle,
    paymentCycleLabel,
    installmentNumber: paymentRequest.installmentNumber || null,
    totalInstallments: paymentRequest.totalInstallments || null,
    paymentDate: timestamp,
    paymentMethod,
    receiptNumber,
    receiptUrl,
    notes,
    createdAt: timestamp,
    requestId: paymentRequest.id,
    recordedAtReception: true,
    recordedById: operator?.uid || '',
    recordedByName: operatorName,
  };

  if (paymentMethod === 'cash') {
    const paymentRef = push(ref(db, 'payments'));
    await set(paymentRef, {
      ...paymentPayload,
      status: 'completed',
      verifiedAt: timestamp,
      verifiedBy: operator?.uid || '',
      verificationDetails: {
        adminId: operator?.uid || '',
        adminName: operatorName,
        verifiedAt: timestamp,
      },
    });
    await remove(ref(db, `paymentRequests/${paymentRequest.id}`));
    touchLmsCache({ studentId: paymentRequest.studentId, admin: true });
    return paymentRef.key;
  }

  const paymentRef = push(ref(db, 'payments'));
  await set(paymentRef, {
    ...paymentPayload,
    status: 'pending',
  });

  await update(ref(db, `paymentRequests/${paymentRequest.id}`), {
    status: 'processing',
    paymentSubmitted: true,
    paymentDate: timestamp,
    lastRecordedBy: operatorName,
  });

  touchLmsCache({ studentId: paymentRequest.studentId, admin: true });
  return paymentRef.key;
};

const normalizeCredentialPart = (value, fallback = 'BRS') => {
  const normalized = String(value || '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();
  return normalized || fallback;
};

const buildCourseCodePrefix = (course = {}) => {
  const explicitCode = normalizeCredentialPart(course.courseCode, '');
  if (explicitCode) return explicitCode.slice(0, 3).padEnd(3, 'X');

  const nameCode = String(course.name || '')
    .split(/\s+/)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, '').charAt(0))
    .join('')
    .toUpperCase();

  return normalizeCredentialPart(nameCode, 'BRS').slice(0, 3).padEnd(3, 'X');
};

const resolveCourseBatch = async ({ courseId, batchId }) => {
  if (!courseId) {
    return {
      course: {},
      batch: null,
      batchNumber: normalizeCredentialPart(batchId, 'BATCH'),
    };
  }

  const courseSnapshot = await get(ref(db, `courses/${courseId}`));
  const course = courseSnapshot.val() || {};
  const batches = course.batches || [];
  const batch =
    batches.find(
      (item, index) =>
        String(item.batchId) === String(batchId) ||
        String(index) === String(batchId) ||
        String(item?.id) === String(batchId),
    ) ||
    batches[0] ||
    null;

  return {
    course,
    batch,
    batchNumber: normalizeCredentialPart(batch?.batchId || batchId, 'BATCH'),
  };
};

export const buildQuickRegisterCredentials = async ({ courseId, batchId }) => {
  const { course, batch, batchNumber } = await resolveCourseBatch({ courseId, batchId });
  const coursePrefix = buildCourseCodePrefix(course);
  const [studentsSnapshot, enrollmentsSnapshot] = await Promise.all([
    get(ref(db, 'students')),
    get(ref(db, 'enrollments')),
  ]);
  const students = Object.values(studentsSnapshot.val() || {});
  const enrollments = Object.values(enrollmentsSnapshot.val() || {});

  const highestStudentIndex = students.reduce((highest, student) => {
    const match = String(student?.studentNumber || '').match(
      new RegExp(`^${coursePrefix}(\\d+)$`),
    );
    return match ? Math.max(highest, Number(match[1] || 0)) : highest;
  }, 0);
  const batchEnrollmentCount = enrollments.filter(
    (enrollment) =>
      String(enrollment?.courseId) === String(courseId) &&
      String(enrollment?.batchId) === String(batch?.batchId || batchId || '0'),
  ).length;
  const nextIndex = Math.max(highestStudentIndex, batchEnrollmentCount) + 1;
  const studentNumber = `${coursePrefix}${String(nextIndex).padStart(3, '0')}`;

  return {
    batchNumber,
    studentNumber,
    password: `${batchNumber}${studentNumber}`,
  };
};

const createStudentAccountDirect = async ({
  firstName,
  lastName,
  email,
  password,
}) => {
  const appName = `kiosk-quick-register-${Date.now()}`;
  const secondaryApp = initializeApp(getApp().options, appName);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      email,
      password,
    );

    try {
      await updateProfile(credential.user, {
        displayName: `${firstName} ${lastName}`.trim(),
      });
    } catch (profileError) {
      console.warn('Could not set displayName on kiosk signup:', profileError);
    }

    const timestamp = new Date().toISOString();
    const payload = {
      uid: credential.user.uid,
      firstName,
      lastName,
      email,
      role: 'student',
      status: 'active',
      createdAt: timestamp,
      updatedAt: timestamp,
      personalDetails: {
        firstName,
        lastName,
        email,
      },
    };

    await Promise.all([
      set(ref(db, `users/${credential.user.uid}`), payload),
      set(ref(db, `students/${credential.user.uid}`), payload),
    ]);

    return credential.user.uid;
  } finally {
    await signOut(secondaryAuth).catch(() => {});
    await deleteApp(secondaryApp).catch(() => {});
  }
};

export const quickRegisterStudent = async ({
  firstName,
  lastName,
  email,
  phone,
  courseId,
  batchId,
  branch,
  operator,
}) => {
  if (!firstName || !lastName || !email) {
    throw new Error('First name, last name, and email are required.');
  }

  const trimmedEmail = email.trim().toLowerCase();
  const { batchNumber, studentNumber, password } = await buildQuickRegisterCredentials({
    courseId,
    batchId: batchId || '0',
  });
  const registeredAt = new Date().toISOString();

  let uid = '';
  try {
    const createManagedUser = httpsCallable(functions, 'createManagedUser');
    const result = await createManagedUser({
      firstName,
      lastName,
      email: trimmedEmail,
      password,
      role: 'student',
      status: 'active',
    });
    uid = result?.data?.uid || '';
  } catch (error) {
    const code = error?.code || '';
    const message = (error?.message || '').toLowerCase();
    const shouldFallback =
      code.includes('not-found') ||
      code.includes('unimplemented') ||
      message.includes('function');

    if (!shouldFallback) {
      throw error;
    }

    uid = await createStudentAccountDirect({
      firstName,
      lastName,
      email: trimmedEmail,
      password,
    });
  }

  if (!uid) {
    throw new Error('Could not create student account.');
  }

  await update(ref(db, `students/${uid}`), {
    personalDetails: {
      firstName,
      lastName,
      email: trimmedEmail,
      phone: phone || '',
      branchId: branch?.id || '',
      branchName: branch?.name || '',
      branch: branch?.slug || branch?.name || '',
    },
    branchId: branch?.id || '',
    branchName: branch?.name || '',
    branch: branch?.slug || branch?.name || '',
    batchNumber,
    studentNumber,
    registrationDate: registeredAt,
    registrationSource: 'reception_kiosk',
    registeredById: operator?.uid || '',
    registeredByName:
      operator?.displayName ||
      `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
      operator?.email ||
      'Reception',
    updatedAt: registeredAt,
  });

  await update(ref(db, `users/${uid}`), {
    phone: phone || '',
    branchId: branch?.id || '',
    branchName: branch?.name || '',
    branch: branch?.slug || branch?.name || '',
    batchNumber,
    studentNumber,
    updatedAt: registeredAt,
  });

  const academyStudentId = await ensureStudentAcademyId({ userId: uid });

  let enrollmentId = '';
  if (courseId) {
    enrollmentId = await assignStudentToBatch({
      studentId: uid,
      courseId,
      batchId: batchId || '0',
    });
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const loginUrl = origin ? `${origin}/login` : '/login';
  const passUrl = academyStudentId
    ? buildStudentQrResolverUrl(academyStudentId)
    : '';

  return {
    uid,
    academyStudentId,
    batchNumber,
    studentNumber,
    email: trimmedEmail,
    phone: phone || '',
    password,
    loginUrl,
    passUrl,
    enrollmentId,
    studentName: `${firstName} ${lastName}`.trim(),
  };
};

export const recordAdhocReceptionPayment = async ({
  studentId,
  studentName,
  courseId,
  courseName,
  batchId,
  batchName,
  amount,
  paymentMethod,
  receiptNumber = '',
  notes = '',
  receiptFile,
  paymentType = 'monthly',
  paymentCycle = '',
  paymentCycleLabel = '',
  operator,
}) => {
  if (!studentId || !courseId) {
    throw new Error('Student and course are required for payment.');
  }
  const numericAmount = Number(amount || 0);
  if (!numericAmount) {
    throw new Error('Payment amount must be greater than zero.');
  }

  let receiptUrl = '';
  if (receiptFile) {
    const fileRef = storageRef(
      storage,
      `receptionReceipts/adhoc_${studentId}_${Date.now()}_${receiptFile.name}`,
    );
    await uploadBytes(fileRef, receiptFile);
    receiptUrl = await getDownloadURL(fileRef);
  }

  const timestamp = new Date().toISOString();
  const operatorName =
    operator?.displayName ||
    `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
    operator?.email ||
    'Reception';

  const payload = {
    studentId,
    studentName: studentName || '',
    courseId,
    courseName: courseName || '',
    batchId: batchId || '',
    batchName: batchName || '',
    amount: numericAmount,
    paymentType: paymentType || 'adhoc',
    paymentCycle,
    paymentCycleLabel,
    paymentDate: timestamp,
    paymentMethod,
    receiptNumber,
    receiptUrl,
    notes,
    createdAt: timestamp,
    recordedAtReception: true,
    recordedById: operator?.uid || '',
    recordedByName: operatorName,
    status: paymentMethod === 'cash' ? 'completed' : 'pending',
  };

  if (paymentMethod === 'cash') {
    payload.verifiedAt = timestamp;
    payload.verifiedBy = operator?.uid || '';
    payload.verificationDetails = {
      adminId: operator?.uid || '',
      adminName: operatorName,
      verifiedAt: timestamp,
    };
  }

  const paymentRef = push(ref(db, 'payments'));
  await set(paymentRef, payload);
  return paymentRef.key;
};

export const logEnquiry = async ({
  firstName,
  lastName = '',
  phone,
  interestCourseId = '',
  interestCourseName = '',
  notes = '',
  operator,
}) => {
  if (!firstName || !phone) {
    throw new Error('Name and phone are required for enquiry.');
  }

  const timestamp = new Date().toISOString();
  const operatorName =
    operator?.displayName ||
    `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim() ||
    operator?.email ||
    'Reception';

  const enquiryRef = push(ref(db, 'enquiries'));
  await set(enquiryRef, {
    id: enquiryRef.key,
    firstName,
    lastName,
    phone,
    interestCourseId,
    interestCourseName,
    notes,
    status: 'new',
    source: 'reception_kiosk',
    capturedAt: timestamp,
    capturedById: operator?.uid || '',
    capturedByName: operatorName,
  });

  return enquiryRef.key;
};
